import { alpha, Box, Tooltip, Typography, useTheme } from '@mui/material'
import { useMemo } from 'react'
import type { NewDeal } from 'types'

// ─── Step definitions ────────────────────────────────────────────────

interface FlowStep {
  key: string
  label: string
  check: (deal: NewDeal, document?: any) => boolean
}

const STANDARD_STEPS: FlowStep[] = [
  {
    key: 'products',
    label: 'Products',
    check: deal => Array.isArray(deal?.products) && deal.products.length > 0,
  },
  {
    key: 'approval_requested',
    label: 'Requested',
    check: deal => !!deal?.isRequested || !!deal?.isApproved,
  },
  {
    key: 'approved',
    label: 'Approved',
    check: deal => !!deal?.isApproved,
  },
  {
    key: 'contract_created',
    label: 'Created',
    check: deal => !!deal?.contractId && deal.contractId !== '',
  },
  {
    key: 'links_generated',
    label: 'Sent',
    check: deal => !!deal?.linksGenerated,
  },
  {
    key: 'authorized_signed',
    label: 'Auth Signed',
    check: (_deal, document) => {
      const recs = document?.recipients ?? []
      const auth = recs.find((r: any) => r.role === 'Authorized Personnel' || r.roles?.includes('Authorized Personnel'))
      return auth?.has_completed === true
    },
  },
  {
    key: 'client_signed',
    label: 'Client Signed',
    check: (_deal, document) => {
      const recs = document?.recipients ?? []
      const client = recs.find((r: any) => r.role === 'Client' || r.roles?.includes('Client'))
      return client?.has_completed === true
    },
  },
  {
    key: 'deal_won',
    label: 'Won',
    check: deal => deal?.stage === 'Deal Won',
  },
]

const NO_SIGNATURE_STEPS: FlowStep[] = [
  {
    key: 'products',
    label: 'Products',
    check: deal => Array.isArray(deal?.products) && deal.products.length > 0,
  },
  {
    key: 'contract_created',
    label: 'Created',
    check: deal => !!deal?.contractId && deal.contractId !== '',
  },
  {
    key: 'deal_won',
    label: 'Completed',
    check: deal => deal?.stage === 'Deal Won' || deal?.status === 'Completed Internally',
  },
]

// ─── Component ───────────────────────────────────────────────────────

interface ContractFlowTimelineProps {
  deal: NewDeal
  document?: any
}

const ContractFlowTimeline = ({ deal, document }: ContractFlowTimelineProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const steps = deal?.noSignature ? NO_SIGNATURE_STEPS : STANDARD_STEPS

  const filteredSteps = useMemo(() => {
    if (deal?.noSignature) return steps
    if (!deal?.needsApproval) {
      return steps.filter(s => s.key !== 'approval_requested' && s.key !== 'approved')
    }
    return steps
  }, [deal?.noSignature, deal?.needsApproval, steps])

  const stepStates = useMemo(() => {
    let foundCurrent = false
    return filteredSteps.map(step => {
      const completed = step.check(deal, document)
      const isCurrent = !completed && !foundCurrent
      if (isCurrent) foundCurrent = true
      return { ...step, completed, isCurrent }
    })
  }, [deal, document, filteredSteps])

  const activeIndex = stepStates.findIndex(s => !s.completed)
  const allComplete = activeIndex === -1
  const completedCount = stepStates.filter(s => s.completed).length

  // Theme-aware colors
  const done = 'text.success'
  const active = 'text.highlight'
  const track = alpha(theme.palette.text.primary, 0.06)
  const dotPending = alpha(theme.palette.text.primary, 0.15)
  const textDim = alpha(theme.palette.text.primary, 0.3)

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        width: '100%',
        py: 0.75,
        px: 0.5,
      }}
    >
      {stepStates.map((step, idx) => {
        const isLast = idx === stepStates.length - 1
        const isActive = idx === activeIndex
        const isPast = step.completed

        // Dot color
        const dotColor = isPast || allComplete ? done : isActive ? active : dotPending
        // Label color
        const labelColor = isPast || allComplete ? done : isActive ? active : textDim

        return (
          <Box
            key={step.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: isLast ? '0 0 auto' : '1 1 0',
              minWidth: 0,
            }}
          >
            {/* Step node: dot + label */}
            <Tooltip
              title={step.label}
              placement="top"
              arrow
              slotProps={{
                tooltip: { sx: { fontSize: 11, py: 0.25, px: 1 } },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 44,
                  cursor: 'default',
                }}
              >
                {/* Dot */}
                <Box
                  sx={{
                    width: isPast || allComplete ? 8 : isActive ? 10 : 6,
                    height: isPast || allComplete ? 8 : isActive ? 10 : 6,
                    borderRadius: '50%',
                    bgcolor: dotColor,
                    transition: 'all 0.3s ease',
                    boxShadow: isActive
                      ? `0 0 0 3px ${isDark ? 'rgba(107,185,240,0.2)' : 'rgba(107,185,240,0.15)'}`
                      : 'none',
                    ...(isActive && {
                      animation: 'stepPulse 2.5s ease-in-out infinite',
                      '@keyframes stepPulse': {
                        '0%, 100%': {
                          boxShadow: `0 0 0 3px ${isDark ? 'rgba(107,185,240,0.2)' : 'rgba(107,185,240,0.15)'}`,
                        },
                        '50%': {
                          boxShadow: `0 0 0 5px ${isDark ? 'rgba(107,185,240,0.1)' : 'rgba(107,185,240,0.08)'}`,
                        },
                      },
                    }),
                  }}
                />
                {/* Label */}
                <Typography
                  sx={{
                    fontSize: 9,
                    fontWeight: isPast || isActive ? 600 : 400,
                    color: labelColor,
                    mt: 0.4,
                    lineHeight: 1,
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
            </Tooltip>

            {/* Connector */}
            {!isLast && (
              <Box
                sx={{
                  flex: 1,
                  height: 1.5,
                  minWidth: 12,
                  mx: 0.25,
                  mt: '-11px',
                  borderRadius: 1,
                  position: 'relative',
                  bgcolor: track,
                  overflow: 'hidden',
                }}
              >
                {/* Filled portion */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: isPast ? '100%' : '0%',
                    bgcolor: done,
                    borderRadius: 1,
                    transition: 'width 0.4s ease',
                  }}
                />
              </Box>
            )}
          </Box>
        )
      })}

      {/* Minimal progress fraction */}
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 500,
          color: allComplete ? done : textDim,
          ml: 1,
          mt: '-11px',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          letterSpacing: '0.02em',
        }}
      >
        {completedCount}/{stepStates.length}
      </Typography>
    </Box>
  )
}

export default ContractFlowTimeline
